"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { m } from "framer-motion";
import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";

export default function ImageCard({
  image,
  topic,
  text,
  frameworkButton,
  frameworks,
  tag,
  tags,
  tagClassName = "",
  hoverOverlayColor = "",
}) {
  const isdark = useIsDark();
  const [isHovered, setIsHovered] = useState(false);
  const [isFrameworkButton, setIsFrameworkButton] = useState(false);

  const halfOverlay = isdark
    ? "linear-gradient(0deg, rgba(26, 26, 26, 0.8) 31.65%, rgba(0, 0, 0, 0) 100%)"
    : "linear-gradient(0deg, #FFFFFF 31.65%, rgba(255, 255, 255, 0) 100%)";

  const tagsToRender = tags || (tag ? [tag] : []);

  // If parent sends a value, override local state
  useEffect(() => {
    if (frameworkButton !== undefined) {
      setIsFrameworkButton(frameworkButton);
    }
  }, [frameworkButton]);

  return (
    <m.div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      initial={{ scale: 1 }}
      whileHover={{ scale: 1.07 }}
      transition={{ type: "spring", mass: 1, stiffness: 80, damping: 20 }}
      className="relative w-full aspect-396/290 rounded-[15px] overflow-hidden mt-5"
    >
      <Image
        src={image || "/assets/placeholder.png"}
        alt="images"
        fill
        sizes="(max-width: 640px) 100vw,
           (max-width: 1024px) 70vw,
           600px"
        quality={90}
        // placeholder="blur"
        className="object-cover"
      />

      {/* Top-left white tags */}
      {tagsToRender.length > 0 && (
        <m.div
          initial={{ opacity: 1, y: 0 }}
          animate={isHovered ? { opacity: 0, y: -8 } : { opacity: 1, y: 0 }}
          transition={{ type: "spring", mass: 1, stiffness: 120, damping: 18 }}
          className="absolute top-[10px] left-[10px] z-20 flex flex-wrap gap-2"
        >
          {tagsToRender.map((tagItem, index) => (
            <span
              key={index}
              className={[
                "inline-block rounded-[10px] bg-white text-black text-[12px] leading-none",
                "px-3 py-2 shadow-md",
                "font-alexandria font-light",
                tagClassName,
              ].join(" ")}
            >
              {tagItem}
            </span>
          ))}
        </m.div>
      )}

      <div className="absolute bottom-4 w-full px-5 mobile:px-2 sm:px-5 z-20">
        <div className="flex justify-between items-center">
          <p
            className={twMerge(
              "font-alexandria font-normal text-[16px] md:text-[18px] xl:text-xl 3xl:text-2xl text-transparent bg-clip-text bg-gradient-to-r dark:from-[#FC4FF6] from-[#9C00BB] dark:to-[#FFFFFF] to-[#1A1A1A]",
              isHovered && "text-white"
            )}
          >
            {topic}
          </p>
          {isFrameworkButton && (
            <button
              className={twMerge(
                "font-alexandria font-light text-[12px] 3xl:text-[14px] rounded-[10px] px-2 py-1",
                isHovered
                  ? "bg-white/14 text-white"
                  : "bg-[#FC4FF6]/14 text-[#9E0098]"
              )}
            >
              {frameworks}
            </button>
          )}
        </div>
        <p
          className={twMerge(
            "font-alexandria font-light text-[14px] md:text-[16px] xl:text-[17px] 3xl:text-xl 3xl:mt-2",
            isdark ? "text-white" : "text-[#434343]",
            isHovered && "text-white"
          )}
        >
          {text}
        </p>
      </div>
      {/* full dark overlay for dark theme */}
      {isdark && <div className="absolute inset-0 bg-[rgba(0,0,0,0.5)] z-1" />}

      {/* semi transparent half overlay */}
      <m.div
        initial={{
          opacity: 1,
          height: 163,
          background: halfOverlay,
        }}
        animate={
          isHovered
            ? { height: "100%", background: hoverOverlayColor || "#1A1A1A99" }
            : {
                height: 163,
                background: halfOverlay,
              }
        }
        transition={{ type: "spring", mass: 1, stiffness: 80, damping: 20 }}
        className="absolute bottom-0 w-full"
      />

      {/* top right corner icon */}
      <m.div
        initial={{ opacity: 0, x: 10 }}
        animate={isHovered ? { opacity: 1, x: 0 } : { opacity: 0, x: 10 }}
        transition={{ type: "spring", mass: 1, stiffness: 100, damping: 20 }}
        className="absolute top-[10px] right-[10px]"
      >
        <Image
          src="/assets/checkLive.svg"
          alt="check live button"
          width={25}
          height={25}
          sizes="25px"
          priority={false}
        />
      </m.div>
    </m.div>
  );
}
